<template>
  <v-container fluid class="pa-0">
    <v-card>
      <!-- Overall Statistics Row -->
      <v-row>
        <v-col cols="12" md="2">
          <div class="fee-stat-container">
            <div class="fee-stat-title">Total Fees</div>
            <div class="fee-stat-data">
              {{ feesTotal }}
            </div>
            <div>{{ averageFee }}</div>
          </div>
        </v-col>
        <v-col cols="12" md="1">
          <div class="fee-stat-container">
            <div class="fee-stat-title">Total Cases</div>
            <div class="fee-stat-data">
              {{ totalCasesResolved }}
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="2">
          <div class="fee-stat-container">
            <div class="fee-stat-title">Avg Cases Per Month</div>
            <div class="fee-stat-data">
              {{ averageCasesResolved }}
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="1" v-if="isAdmin">
          <div class="mr-2">
            <v-switch
              class=""
              label="Show All"
              v-model="showAllFees"
            ></v-switch>
          </div>
        </v-col>
        <v-col cols="12" md="1" v-if="isAdmin">
          <div class="mr-2">
            <v-switch
              class=""
              label="Exclude Shared Fees"
              v-model="excludeSharedFees"
            ></v-switch>
          </div>
        </v-col>
        <v-col cols="12" md="1" v-if="isAdmin">
          <div class="mr-2">
            <v-autocomplete
              :items="officeOptions"
              v-model="selectedOffice"
            ></v-autocomplete>
          </div>
        </v-col>
        <v-col cols="12" md="1" v-if="isAdmin">
          <div class="mr-2">
            <v-autocomplete
              :items="stateOptions"
              v-model="selectedState"
            ></v-autocomplete>
          </div>
        </v-col>
        <v-col cols="12" md="1" v-if="isAdmin">
          <div class="mr-2">
            <v-autocomplete
              :items="referralSources"
              v-model="selectedReferralSource"
            ></v-autocomplete>
          </div>
        </v-col>
      </v-row>

      <!-- Case Group Statistics -->
      <v-row v-if="caseGroupStats.length > 0">
        <v-col cols="12">
          <v-card class="mb-4" outlined>
            <v-card-title class="pb-2">
              <span class="text-h6">Statistics by Case Group</span>
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="groupStatsView" mandatory>
                <v-btn small value="summary">Summary</v-btn>
                <v-btn small value="detailed">Detailed</v-btn>
              </v-btn-toggle>
            </v-card-title>

            <!-- Summary View -->
            <v-row v-if="groupStatsView === 'summary'" class="px-4 pb-4">
              <v-col
                v-for="groupStat in caseGroupStats"
                :key="groupStat.group"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <v-card class="group-stat-card" outlined>
                  <v-card-text class="text-center">
                    <div class="group-stat-title">
                      {{ groupStat.group || "Unspecified" }}
                    </div>
                    <div class="group-stat-main">
                      {{ formatMoney(groupStat.totalFees) }}
                    </div>
                    <div class="group-stat-sub">
                      {{ groupStat.totalCases }} cases ·
                      {{ formatMoney(groupStat.averageFeePerCase) }} avg/case
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="fee-breakdown">
                      <div class="fee-breakdown-row">
                        <div class="fee-breakdown-item">
                          <div class="breakdown-value info--text">
                            {{ formatMoney(groupStat.preLitigationFees) }}
                          </div>
                          <div class="breakdown-label">Pre-Litigation</div>
                        </div>
                        <div class="fee-breakdown-item">
                          <div class="breakdown-value error--text">
                            {{ formatMoney(groupStat.litigationFees) }}
                          </div>
                          <div class="breakdown-label">Litigation</div>
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Detailed View -->
            <v-data-table
              v-if="groupStatsView === 'detailed'"
              :headers="groupStatsHeaders"
              :items="caseGroupStats"
              class="elevation-0"
              :items-per-page="10"
              :sort-by="['totalFees']"
              :sort-desc="[true]"
            >
              <template v-slot:item.totalFees="{ item }">
                {{ formatMoney(item.totalFees) }}
              </template>
              <template v-slot:item.averageFeePerCase="{ item }">
                {{ formatMoney(item.averageFeePerCase) }}
              </template>
              <template v-slot:item.preLitigationFees="{ item }">
                {{ formatMoney(item.preLitigationFees) }}
              </template>
              <template v-slot:item.litigationFees="{ item }">
                {{ formatMoney(item.litigationFees) }}
              </template>
              <template v-slot:item.group="{ item }">
                {{ item.group || "Unspecified" }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Chart Row -->
      <v-row>
        <v-col cols="12">
          <v-card class="mb-6" outlined>
            <v-card-title class="pb-2">
              <span class="text-h6">Fee Analytics</span>
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="chartView" mandatory>
                <v-btn small value="timeline">Timeline</v-btn>
                <v-btn small value="feeAmounts">Fee Amounts</v-btn>
              </v-btn-toggle>
            </v-card-title>

            <!-- Timeline Chart -->
            <div v-if="chartView === 'timeline'" class="chart-section">
              <div class="chart-subtitle">
                Cases Resolved by Month (with Case Group Breakdown)
              </div>
              <org-cases-resolved-by-month
                :options="enhancedTimelineChartOptions"
              />
            </div>

            <!-- Fee Amounts Chart -->
            <div v-if="chartView === 'feeAmounts'" class="chart-section">
              <div class="chart-subtitle">
                Monthly Fee Amounts by Case Group
              </div>
              <org-cases-resolved-by-month :options="feeAmountsChartOptions" />
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="search"
            append-icon="mdi-search"
            label="Search"
            hide-details
            single-line
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Fees Data Table -->
      <v-data-table
        :headers="headers"
        :items="fees"
        class="elevation-1"
        :footer-props="{
          'items-per-page-options': [50, 100, -1]
        }"
        :sort-by="['date']"
        :sort-desc="[true]"
        :search="search"
        :item-key="Math.random(10).toString()"
        :loading="$apollo.loading"
        height="inherit"
      >
        <template v-slot:item.caseId="props">
          <a
            class="go-to-case-link mr-2"
            target="_blank"
            :href="
              `https://cs-web.airdesksolutions.com/SA/CaseSummary.aspx?CaseID=${props.item.caseId}`
            "
          >
            <v-icon size="15">mdi-open-in-new</v-icon>
          </a>
        </template>
        <template v-slot:item.fee="props">
          {{ formatMoney(props.item.fee) }}
        </template>
        <template v-slot:header.dateSettled="{ header }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ header.text }}</span>
            </template>
            <span>{{ header.tooltip }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.dateSettled="props">
          <v-icon
            :color="props.item.dateSettled ? 'success' : 'grey lighten-1'"
            size="20"
          >
            {{
              props.item.dateSettled ? "mdi-check-circle" : "mdi-circle-outline"
            }}
          </v-icon>
        </template>
        <template v-slot:item.sharedFee="props">
          <v-icon
            :color="props.item.sharedFee ? 'success' : 'grey lighten-1'"
            size="20"
          >
            {{
              props.item.sharedFee ? "mdi-check-circle" : "mdi-circle-outline"
            }}
          </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import accounting from "accounting";
import filter from "lodash/filter";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import sumBy from "lodash/sumBy";
import OrgCasesResolvedByMonth from "../charts/OrgCasesResolvedByMonth.vue";
import { hasRole } from "../util";
import uniq from "lodash/uniq";

export default {
  data() {
    return {
      search: "",
      pagination: { sortBy: "date", descending: true },
      selectedOffice: null,
      selectedState: null,
      selectedReferralSource: null,
      groupStatsView: "summary",
      officeOptions: [
        { text: "All Markets", value: null },
        {
          text: "(2 - WA Main) Spokane Valley Office",
          value: "(2 - WA Main) Spokane Valley Office"
        },
        {
          text: "(1 - UT Main) Sandy Office",
          value: "(1 - UT Main) Sandy Office"
        },
        {
          text: "(3 - ID Main) Meridian Office",
          value: "(3 - ID Main) Meridian Office"
        },
        { text: "Sea-Tac", value: "Sea-Tac" },
        { text: "Other", value: "Other" },
        { text: "Wyoming", value: "Wyoming" },
        { text: "Montana", value: "Montana" },
        { text: "Colorado", value: "Colorado" },
        { text: "Oregon", value: "Oregon" }
      ],
      showAllFees: true,
      excludeSharedFees: false,
      chartView: "timeline"
    };
  },
  components: {
    OrgCasesResolvedByMonth
  },
  apollo: {
    organization: {
      query: gql`
        query($id: ID!, $startDate: String!, $endDate: String!) {
          organization(id: $id) {
            id
            name
            teams {
              id
              name
            }
            sa_individual_fees_in_period_by_attorney(
              startDate: $startDate
              endDate: $endDate
            ) {
              clientName
              attorney
              caseId
              fee
              date
              office
              state
              referralSource
              feeType
              sharedFee
              caseGroup
              dateSettled
            }
          }
        }
      `,
      variables() {
        return {
          id: 2,
          startDate: this.startDate,
          endDate: this.endDate
        };
      },
      fetchPolicy: "no-cache",
      skip() {
        return this.startDate === null || this.endDate === null;
      }
    },
    advertisingSources: {
      query: gql`
        query DashFeesListReferralSources {
          advertisingSources {
            contact
            contactId
          }
        }
      `
    }
  },
  computed: {
    ...mapGetters(["selectedTeam", "startDate", "endDate", "currentUser"]),
    headers() {
      let headers = [
        { text: "Action", value: "caseId" },
        { text: "Client", value: "clientName" },
        { text: "Attorney", value: "attorney" },
        { text: "Fee", value: "fee" },
        { text: "Group", value: "caseGroup" },
        { text: "Fee Type", value: "feeType" },
        { text: "Shared Fee", value: "sharedFee" },
        { text: "Office", value: "office" },
        { text: "State", value: "state" },
        { text: "Fee Date", value: "date" },
        {
          text: "FS",
          value: "dateSettled",
          tooltip:
            "Fully Settled - Case has no additional sources of compensation to pursue (e.g., underinsured liability coverage, additional liability policies)"
        }
      ];

      if (this.isAdmin) {
        headers.splice(3, 0, { text: "Source", value: "referralSource" });
      }

      return headers;
    },
    groupStatsHeaders() {
      return [
        { text: "Case Group", value: "group" },
        { text: "Total Cases", value: "totalCases" },
        { text: "Total Fees", value: "totalFees" },
        { text: "Average Fee per Case", value: "averageFeePerCase" },
        { text: "Percentage of Total", value: "percentageOfTotal" },
        { text: "Pre-Litigation Fees", value: "preLitigationFees" },
        { text: "Litigation Fees", value: "litigationFees" }
      ];
    },
    showAll() {
      if (!this.currentUser) return false;
      if (this.showAllFees === false) {
        return false;
      }
      return (
        hasRole(this.currentUser, "admin") ||
        hasRole(this.currentUser, "marketing")
      );
    },
    isAdmin() {
      if (!this.currentUser) return false;
      return (
        hasRole(this.currentUser, "admin") ||
        hasRole(this.currentUser, "marketing")
      );
    },
    referralSources() {
      if (!this.advertisingSources) return [];
      let options = this.advertisingSources.map(s => {
        return { text: s.contact, value: s.contact };
      });
      options.unshift({ text: "All Sources", value: null });
      return options;
    },
    stateOptions() {
      if (!this.organization) return [];
      let options = uniq(
        this.organization.sa_individual_fees_in_period_by_attorney.map(
          fee => fee.state
        )
      ).map(state => {
        return { text: state, value: state };
      });
      options.unshift({ text: "All States", value: null });
      return options;
    },
    fees() {
      if (!this.organization) return [];

      // First, filter fees based on the selected team or attorney
      let fees = filter(
        this.organization.sa_individual_fees_in_period_by_attorney,
        f => {
          if (this.team === "LS") {
            return (
              f.attorney === "LS" ||
              f.attorney === "KNP" ||
              f.attorney === "EMR"
            );
          } else if (this.team === "CLP") {
            return f.attorney === "CLP" || f.attorney === "PPR";
          } else if (this.team === "ACS") {
            return (
              f.attorney === "ACS" ||
              f.attorney === "TRS" ||
              f.attorney === "RAS"
            );
          } else {
            return f.attorney === this.team;
          }
        }
      );

      // If showing all fees, use all fees
      if (this.showAll) {
        fees = this.organization.sa_individual_fees_in_period_by_attorney;
      }
      // If not showing all fees, include shared case IDs
      else {
        // Get all case IDs from the filtered fees
        const caseIds = fees.map(fee => fee.caseId);

        // Check if the attorney is a litigation attorney
        const isLitigationAttorney = [
          "MCA",
          "ECD",
          "JRT",
          "LCK",
          "MT",
          "LDM"
        ].includes(this.team);

        // If we're filtering for a specific attorney, include all fees with the same case IDs
        if (this.team && isLitigationAttorney) {
          const sharedFees = filter(
            this.organization.sa_individual_fees_in_period_by_attorney,
            fee => caseIds.includes(fee.caseId) && fee.attorney !== this.team
          );

          // Add the shared fees to the filtered fees
          fees = [...fees, ...sharedFees];
        }
      }

      // Apply additional filters
      if (this.excludeSharedFees) {
        fees = filter(
          fees,
          f => !f.sharedFee || (f.sharedFee && f.feeType === "Litigation")
        );
      }
      if (this.selectedOffice) {
        fees = filter(fees, f => f.office === this.selectedOffice);
      }
      if (this.selectedState) {
        fees = filter(fees, f => f.state === this.selectedState);
      }
      if (this.selectedReferralSource) {
        fees = filter(
          fees,
          f => f.referralSource === this.selectedReferralSource
        );
      }
      return fees;
    },
    // Aggregate fees by case to get proper case-level statistics
    aggregatedFeesByCase() {
      if (!this.fees) return [];

      const feesByCase = groupBy(this.fees, "caseId");

      return Object.keys(feesByCase).map(caseId => {
        const caseFees = feesByCase[caseId];
        const totalFeeForCase = sumBy(caseFees, "fee");
        const firstFee = caseFees[0]; // Use first fee for case metadata

        return {
          caseId,
          totalFee: totalFeeForCase,
          caseGroup: firstFee.caseGroup,
          attorney: firstFee.attorney,
          clientName: firstFee.clientName,
          office: firstFee.office,
          state: firstFee.state,
          date: firstFee.date,
          feeCount: caseFees.length
        };
      });
    },
    // Calculate statistics by case group
    caseGroupStats() {
      if (!this.aggregatedFeesByCase.length) return [];

      const groupedByCaseGroup = groupBy(
        this.aggregatedFeesByCase,
        "caseGroup"
      );
      const totalFeesAllGroups = sumBy(this.aggregatedFeesByCase, "totalFee");

      return Object.keys(groupedByCaseGroup)
        .map(group => {
          const groupCases = groupedByCaseGroup[group];
          const totalFees = sumBy(groupCases, "totalFee");
          const totalCases = groupCases.length;
          const averageFeePerCase = totalCases > 0 ? totalFees / totalCases : 0;
          const percentageOfTotal =
            totalFeesAllGroups > 0
              ? ((totalFees / totalFeesAllGroups) * 100).toFixed(1) + "%"
              : "0%";

          // Calculate fees breakdown by feeType for this group
          const groupFees = this.fees.filter(fee => fee.caseGroup === group);

          // Group fees by caseId to determine case-level classification
          const feesByCaseId = groupBy(groupFees, "caseId");
          let preLitigationFees = 0;
          let litigationFees = 0;

          Object.keys(feesByCaseId).forEach(caseId => {
            const caseFees = feesByCaseId[caseId];
            const totalCaseFees = sumBy(caseFees, "fee");

            // If any fee for this case is Litigation, count all fees as Litigation
            const hasLitigationFee = caseFees.some(
              fee => fee.feeType === "Litigation"
            );

            if (hasLitigationFee) {
              litigationFees += totalCaseFees;
            } else {
              preLitigationFees += totalCaseFees;
            }
          });

          return {
            group,
            totalCases,
            totalFees,
            averageFeePerCase,
            percentageOfTotal,
            preLitigationFees,
            litigationFees
          };
        })
        .sort((a, b) => b.totalFees - a.totalFees); // Sort by total fees descending
    },
    team() {
      if (!this.organization) return null;
      if (this.selectedTeam) {
        return filter(
          this.organization.teams,
          t => t.id === this.selectedTeam
        )[0].name;
      } else {
        return "No Team";
      }
    },
    feesTotal() {
      if (!this.organization) return accounting.formatMoney(0);
      let total_fees = this.fees.reduce((prev, cur) => {
        return prev + cur.fee;
      }, 0);
      return accounting.formatMoney(total_fees);
    },
    feesByMonth() {
      if (!this.organization) return [];
      let groupByCaseId = groupBy(this.fees, "caseId");
      let caseIdKeys = Object.keys(groupByCaseId);
      let mapCasesToMonths = caseIdKeys.map(caseId => {
        let fees = groupByCaseId[caseId];
        let firstFee = fees[0];
        let month = firstFee.date.split("-")[1];
        let year = firstFee.date.split("-")[0];
        let monthName = Date.UTC(year, month - 1, 1);
        return {
          month: monthName,
          caseId: caseId
        };
      });
      let casesGroupedByMonth = groupBy(mapCasesToMonths, "month");
      let monthKeys = Object.keys(casesGroupedByMonth);
      let casesByMonth = monthKeys.map(m => {
        return [
          parseInt(m),
          casesGroupedByMonth[m].reduce(prev => {
            return prev + 1;
          }, 0)
        ];
      });
      return {
        name: "Cases Resolved by Month",
        data: sortBy(casesByMonth, ([month]) => month)
      };
    },
    averageCasesResolved() {
      if (!this.feesByMonth || !this.feesByMonth.data) return 0;
      return (
        this.feesByMonth.data.reduce((prev, cur) => {
          return prev + cur[1];
        }, 0) / this.feesByMonth.data.length
      ).toFixed(2);
    },
    averageFee() {
      if (!this.aggregatedFeesByCase.length) return accounting.formatMoney(0);
      const totalFees = sumBy(this.aggregatedFeesByCase, "totalFee");
      const averageFeePerCase = totalFees / this.aggregatedFeesByCase.length;
      return accounting.formatMoney(averageFeePerCase);
    },
    totalCasesResolved() {
      return this.aggregatedFeesByCase.length;
    },
    feesByMonthChartOptions() {
      return {
        series: this.feesByMonth
      };
    },
    enhancedTimelineChartOptions() {
      if (!this.aggregatedFeesByCase.length) return { series: [] };

      // Define a beautiful color palette
      const colorPalette = [
        "#4A90E2", // Blue
        "#7ED321", // Green
        "#F5A623", // Orange
        "#D0021B", // Red
        "#9013FE", // Purple
        "#50E3C2", // Teal
        "#B8E986", // Light Green
        "#F8E71C", // Yellow
        "#BD10E0", // Magenta
        "#B4A7D6", // Lavender
        "#417505", // Dark Green
        "#9AA0A6" // Gray
      ];

      // For timeline chart, we need to find the earliest fee date for each case
      // to count it only once in the month it was first resolved
      const caseFirstFeeDate = {};
      this.fees.forEach(fee => {
        if (
          !caseFirstFeeDate[fee.caseId] ||
          fee.date < caseFirstFeeDate[fee.caseId]
        ) {
          caseFirstFeeDate[fee.caseId] = fee.date;
        }
      });

      // Create case data using only the first fee date for each case
      const casesForTimeline = this.aggregatedFeesByCase.map(caseData => ({
        ...caseData,
        date: caseFirstFeeDate[caseData.caseId] // Use the earliest fee date
      }));

      // Group cases by month and case group using the earliest fee date
      const casesByMonthAndGroup = {};
      const totalsByMonth = {};

      casesForTimeline.forEach(caseData => {
        const month = caseData.date.split("-")[1];
        const year = caseData.date.split("-")[0];
        const monthKey = Date.UTC(year, month - 1, 1);
        const group = caseData.caseGroup || "Unspecified";

        // Track by group
        if (!casesByMonthAndGroup[group]) {
          casesByMonthAndGroup[group] = {};
        }
        if (!casesByMonthAndGroup[group][monthKey]) {
          casesByMonthAndGroup[group][monthKey] = 0;
        }
        casesByMonthAndGroup[group][monthKey]++;

        // Track totals
        if (!totalsByMonth[monthKey]) {
          totalsByMonth[monthKey] = 0;
        }
        totalsByMonth[monthKey]++;
      });

      // Get all unique months and sort them
      const allMonths = Object.keys(totalsByMonth)
        .map(m => parseInt(m))
        .sort((a, b) => a - b);

      // Create series for each case group (as stacked columns)
      const groupSeries = Object.keys(casesByMonthAndGroup).map(
        (group, index) => {
          const data = allMonths.map(month => {
            return [month, casesByMonthAndGroup[group][month] || 0];
          });

          return {
            name: group,
            type: "column",
            data: data,
            stack: "cases",
            color: colorPalette[index % colorPalette.length],
            borderRadius: 2
          };
        }
      );

      // Create total line series
      const totalData = allMonths.map(month => [month, totalsByMonth[month]]);
      const totalSeries = {
        name: "Total Cases",
        type: "line",
        data: totalData,
        color: "#2C3E50",
        lineWidth: 4,
        marker: {
          enabled: true,
          radius: 6,
          fillColor: "#2C3E50",
          lineColor: "#FFFFFF",
          lineWidth: 2
        },
        yAxis: 0,
        zIndex: 10,
        shadow: {
          color: "rgba(44, 62, 80, 0.3)",
          offsetX: 2,
          offsetY: 2,
          opacity: 0.5,
          width: 3
        }
      };

      return {
        chart: {
          type: "column",
          height: 350,
          backgroundColor: "#FAFAFA",
          style: {
            fontFamily: "Roboto, sans-serif"
          }
        },
        title: { text: "" },
        xAxis: {
          type: "datetime",
          title: {
            text: "Month",
            style: {
              color: "#666",
              fontWeight: "600"
            }
          },
          gridLineColor: "#E0E0E0",
          lineColor: "#E0E0E0"
        },
        yAxis: [
          {
            title: {
              text: "Number of Cases",
              style: {
                color: "#666",
                fontWeight: "600"
              }
            },
            min: 0,
            gridLineColor: "#E0E0E0",
            stackLabels: {
              enabled: true,
              style: {
                fontWeight: "bold",
                color: "#2C3E50",
                textOutline: "1px contrast"
              }
            }
          }
        ],
        series: [...groupSeries, totalSeries],
        plotOptions: {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: false
            },
            borderWidth: 0,
            shadow: {
              color: "rgba(0, 0, 0, 0.1)",
              offsetX: 1,
              offsetY: 1,
              opacity: 0.3,
              width: 2
            }
          },
          line: {
            marker: {
              enabled: true,
              symbol: "circle"
            },
            dataLabels: {
              enabled: true,
              style: {
                fontWeight: "bold",
                color: "#2C3E50",
                textOutline: "1px contrast"
              },
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              borderColor: "#2C3E50",
              borderRadius: 3,
              borderWidth: 1,
              padding: 4
            }
          }
        },
        tooltip: {
          shared: true,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderColor: "#E0E0E0",
          borderRadius: 8,
          shadow: true,
          style: {
            fontSize: "12px"
          },
          formatter: function() {
            const date = new Date(this.x).toLocaleDateString("en-US", {
              month: "long",
              year: "numeric"
            });

            let tooltip = '<b style="color: #2C3E50;">' + date + "</b><br/>";

            // Show stacked column data
            const stackedPoints = this.points.filter(
              p => p.series.type === "column"
            );
            if (stackedPoints.length > 0) {
              tooltip += "<br/><b>Cases by Group:</b><br/>";
              stackedPoints.forEach(point => {
                if (point.y > 0) {
                  tooltip +=
                    '<span style="color:' +
                    point.series.color +
                    '; font-size: 14px;">●</span> ' +
                    '<span style="color: #333;">' +
                    point.series.name +
                    ": <b>" +
                    point.y +
                    "</b></span><br/>";
                }
              });
            }

            // Show total line data
            const linePoint = this.points.find(p => p.series.type === "line");
            if (linePoint) {
              tooltip +=
                '<br/><span style="color: #2C3E50; font-weight: bold; font-size: 13px;">Total: ' +
                linePoint.y +
                " cases</span>";
            }

            return tooltip;
          }
        },
        legend: {
          enabled: true,
          align: "center",
          verticalAlign: "bottom",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          borderColor: "#E0E0E0",
          borderRadius: 5,
          borderWidth: 1,
          itemStyle: {
            color: "#333",
            fontWeight: "500"
          }
        }
      };
    },
    feeAmountsChartOptions() {
      if (!this.fees.length) return { series: [] };

      // Use the same color palette for consistency
      const colorPalette = [
        "#4A90E2",
        "#7ED321",
        "#F5A623",
        "#D0021B",
        "#9013FE",
        "#50E3C2",
        "#B8E986",
        "#F8E71C",
        "#BD10E0",
        "#B4A7D6",
        "#417505",
        "#9AA0A6"
      ];

      // Group fees by month and case group using individual fees
      const feesByMonthAndGroup = {};

      this.fees.forEach(fee => {
        const month = fee.date.split("-")[1];
        const year = fee.date.split("-")[0];
        const monthKey = Date.UTC(year, month - 1, 1);
        const group = fee.caseGroup || "Unspecified";

        if (!feesByMonthAndGroup[group]) {
          feesByMonthAndGroup[group] = {};
        }
        if (!feesByMonthAndGroup[group][monthKey]) {
          feesByMonthAndGroup[group][monthKey] = 0;
        }
        feesByMonthAndGroup[group][monthKey] += fee.fee;
      });

      // Convert to series format
      const series = Object.keys(feesByMonthAndGroup).map((group, index) => {
        const monthData = feesByMonthAndGroup[group];
        const data = Object.keys(monthData)
          .map(month => [parseInt(month), monthData[month]])
          .sort((a, b) => a[0] - b[0]);

        return {
          name: group,
          data: data,
          color: colorPalette[index % colorPalette.length],
          borderRadius: 2
        };
      });

      return {
        chart: {
          type: "column",
          height: 350,
          backgroundColor: "#FAFAFA",
          style: {
            fontFamily: "Roboto, sans-serif"
          }
        },
        title: { text: "" },
        xAxis: {
          type: "datetime",
          title: {
            text: "Month",
            style: {
              color: "#666",
              fontWeight: "600"
            }
          },
          gridLineColor: "#E0E0E0",
          lineColor: "#E0E0E0"
        },
        yAxis: {
          title: {
            text: "Fee Amount ($)",
            style: {
              color: "#666",
              fontWeight: "600"
            }
          },
          min: 0,
          gridLineColor: "#E0E0E0",
          labels: {
            formatter: function() {
              return "$" + this.value.toLocaleString();
            },
            style: {
              color: "#666"
            }
          },
          stackLabels: {
            enabled: true,
            style: {
              fontWeight: "bold",
              color: "#2C3E50",
              textOutline: "1px contrast"
            },
            formatter: function() {
              return "$" + this.total.toLocaleString();
            }
          }
        },
        series: series,
        plotOptions: {
          column: {
            stacking: "normal",
            borderWidth: 0,
            shadow: {
              color: "rgba(0, 0, 0, 0.1)",
              offsetX: 1,
              offsetY: 1,
              opacity: 0.3,
              width: 2
            }
          }
        },
        tooltip: {
          shared: true,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderColor: "#E0E0E0",
          borderRadius: 8,
          shadow: true,
          style: {
            fontSize: "12px"
          },
          formatter: function() {
            const date = new Date(this.x).toLocaleDateString("en-US", {
              month: "long",
              year: "numeric"
            });

            let tooltip = '<b style="color: #2C3E50;">' + date + "</b><br/>";
            let total = 0;

            // Show each group's contribution
            if (this.points && this.points.length > 0) {
              tooltip += "<br/><b>Fees by Group:</b><br/>";
              this.points.forEach(point => {
                if (point.y > 0) {
                  tooltip +=
                    '<span style="color:' +
                    point.series.color +
                    '; font-size: 14px;">●</span> ' +
                    '<span style="color: #333;">' +
                    point.series.name +
                    ": <b>$" +
                    point.y.toLocaleString() +
                    "</b></span><br/>";
                  total += point.y;
                }
              });

              tooltip +=
                '<br/><span style="color: #2C3E50; font-weight: bold; font-size: 13px;">Total: $' +
                total.toLocaleString() +
                "</span>";
            }

            return tooltip;
          }
        },
        legend: {
          enabled: true,
          align: "center",
          verticalAlign: "bottom",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          borderColor: "#E0E0E0",
          borderRadius: 5,
          borderWidth: 1,
          itemStyle: {
            color: "#333",
            fontWeight: "500"
          }
        }
      };
    }
  },
  methods: {
    formatMoney(val) {
      return accounting.formatMoney(val);
    }
  }
};
</script>

<style scoped>
/* Container Styling */
.v-container {
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.v-card {
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.v-row {
  margin: 0 !important;
}

td.status {
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  width: 150px;
}

.inventory-title {
  font-size: 36px;
}

.inventory-number {
  font-size: 24px;
}
.fee-stat-container {
  padding: 8px 16px;
  text-align: center;
}
.fee-stat-title {
  font-size: 16px;
}
.fee-stat-data {
  font-size: 30px;
  font-weight: bold;
}
/* Case Group Statistics Styling */
.group-stat-card {
  height: 100%;
  transition: transform 0.2s;
}
.group-stat-card:hover {
  transform: translateY(-2px);
}
.group-stat-title {
  font-size: 14px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
}
.group-stat-main {
  font-size: 24px;
  font-weight: bold;
  color: #1976d2;
  margin-bottom: 4px;
}
.group-stat-sub {
  font-size: 12px;
  color: #888;
  margin-bottom: 2px;
}

/* Fee Breakdown Styling */
.fee-breakdown {
  margin-top: 8px;
}

.fee-breakdown-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.fee-breakdown-item {
  flex: 1;
  min-width: 0;
}

.breakdown-value {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 2px;
}

.breakdown-label {
  font-size: 10px;
  color: #666;
  font-weight: 500;
  line-height: 1.2;
}

/* Chart Section Styling */
.chart-section {
  padding: 16px;
  min-height: 450px;
}
.chart-subtitle {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
  text-align: center;
  font-weight: 500;
}
</style>
